import * as React from 'react'
import { css, styled } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'
import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as env from '@owl-nest/config'

import { footerBreakpoint } from './constants'
import type { PolymorphicComponent, YakComponentOf } from '@owl-nest/yak/extra-runtime'

type ToesProps = {
  className?: string
  lang: api.Lang
  $lang: api.Lang
}

function ToesComponent({ className, lang }: ToesProps) {
  return (
    <div className={className}>
      <LogoLink href={`${env.ULULE_ORIGINS[lang]}/`} aria-label={t('Ulule home')}>
        <plume.logos.ulule.HorizontalLogo />
      </LogoLink>

      <SocialLinks $lang={lang}>
        <li>
          <plume.Link
            href={`https://www.facebook.com/${env.FACEBOOK_ACCOUNTS[lang] ?? env.FACEBOOK_ACCOUNTS.en}`}
            target="_blank"
            title="Facebook"
          >
            <plume.glyphs.thirdParty.Facebook size={14} />
          </plume.Link>
        </li>
        <li>
          <plume.Link href={`https://tiktok.com/@ulule_off`} target="_blank" title="TikTok">
            <plume.glyphs.thirdParty.Tiktok size={14} />
          </plume.Link>
        </li>
        <li>
          <plume.Link href="https://www.instagram.com/ulule/" target="_blank" title="Instagram">
            <plume.glyphs.thirdParty.Instagram size={14} />
          </plume.Link>
        </li>
        <li>
          <plume.Link href="https://www.linkedin.com/company/ulule" target="_blank" title="LinkedIn">
            <plume.glyphs.thirdParty.Linkedin size={15} />
          </plume.Link>
        </li>
        <li>
          <plume.Link href="https://www.youtube.com/user/UluleChouette" target="_blank" title="YouTube">
            <plume.glyphs.thirdParty.Youtube size={15} />
          </plume.Link>
        </li>
      </SocialLinks>

      <LegalLinks $lang={lang}>
        <LinkItem as="li">
          <plume.Link href={env.TERMS_OF_USE_URL}>{t('Terms of use')}</plume.Link>
        </LinkItem>
        <LinkItem as="li">
          <plume.Link href={env.COOKIE_POLICY_URL}>{t('Cookies')}</plume.Link>
        </LinkItem>
        <LinkItem as="li">
          <plume.Link href={env.PRIVACY_URL}>{t('Privacy')}</plume.Link>
        </LinkItem>
      </LegalLinks>
    </div>
  )
}

const toesBreakpointStyle = css`
  align-items: center;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 0px auto;
  max-width: 900px;
  min-height: 68px;
  padding: 0;
  width: 100%;
`

export const Toes = styled(ToesComponent)<{ $lang: string }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0px auto;
  padding: 33px 0;
  position: relative;

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          ${toesBreakpointStyle};
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          ${toesBreakpointStyle};
        }
      `
    }
  }}
`

const LogoLink = styled.a`
  color: ${plume.COLORS.TEXT_PRIMARY};

  &:hover {
    color: ${plume.COLORS.TEXT_PRIMARY};
  }
`

const LinkItem = styled(plume.styles.copy.XS.semiBold)`
  margin: 0 6px;

  ${plume.Link} {
    display: block;
    height: fit-content;
    line-height: 0;
  }
` as YakComponentOf<PolymorphicComponent<'p', plume.styles.copy.CopyProps>>

const SocialLinks = styled.ul<{ $lang: string }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: fit-content;
  justify-content: space-between;
  list-style: none;
  margin: 25px 0 0;
  padding: 0;
  width: 185px;

  ${plume.glyphs.Icon} {
    color: ${plume.COLORS.TEXT_PRIMARY};
    display: inline-block;
    fill-rule: evenodd;
    vertical-align: baseline;
  }

  ${plume.glyphs.thirdParty.Youtube} {
    position: relative;
    top: 2px;
  }

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          margin: 0 auto;
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          margin: 0 auto;
        }
      `
    }
  }}
`

const LegalLinks = styled.ul<{ $lang: string }>`
  align-items: center;
  color: ${plume.COLORS.PRIMARY_GREY_700};
  font-weight: 400; // HACK: Irregular font manipulation
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  list-style: none;
  margin: 0;
  padding: 0;
  margin-top: 25px;

  ${({ $lang }) => {
    if (footerBreakpoint($lang) === 'laptop') {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          margin: 0;
          justify-content: end;
        }
      `
    }
    if (footerBreakpoint($lang) === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          margin: 0;
          justify-content: end;
        }
      `
    }
  }}
`
